import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { VideoFocus, type ChimeEvent } from '@/types';
import {
  DomRoomState as RoomState,
  type EntityMeetingDetail as MeetingDetail
} from '@/utils/api/api';

/**
 * 会議室・ミーティング関連のStore
 */
export const useMeetingStore = defineStore('meeting', () => {
  /** 会議情報 */
  const meeting = ref<MeetingDetail | undefined>(undefined);

  /**
   * 会議情報 - getter
   * @returns 会議室状態
   */
  const meetingRoomState = computed(() => meeting.value?.status?.state);

  /**
   * 会議情報 - getter
   * @returns 二次専門組織
   */
  const secondaryOrganizationMembers = computed(() => meeting.value?.secondaryOrganizationMembers);

  /**
   * 会議情報 - setter
   * @param value
   */
  const setMeeting = (value: MeetingDetail): void => {
    meeting.value = value;
    meetingState.value = value.status?.state;
  };

  /**
   * 会議の状態
   */
  const meetingState = ref<RoomState>(undefined);

  /**
   * 会議の状態 - setter
   * @param value
   */
  const setMeetingState = (value: RoomState) => {
    meetingState.value = value;
  };

  /**
   * Chimeミーティングの状態
   */
  const chimeState = ref<ChimeEvent>({
    event: undefined,
    meetingStatus: undefined
  });

  /**
   * Chimeミーティングの状態 - setter
   * @param value
   */
  const setChimeState = (event: string, meetingStatus: string) => {
    chimeState.value = { event: event, meetingStatus: meetingStatus };
  };

  const isChimeMeetingStarted = computed(() => {
    return chimeState.value.event === 'meetingStartSucceeded';
  });

  const isChimeMeetingEnded = computed(() => {
    return (
      chimeState.value.event === 'meetingEnded' && chimeState.value.meetingStatus === 'MeetingEnded'
    );
  });

  /** 画面共有の状態 */
  const contentShare = ref(false);

  /**
   * 画面共有の状態 - setter
   * @param value
   */
  const setContentShare = (value: boolean): void => {
    contentShare.value = value;
  };

  /** 録画状態 */
  const recording = ref(false);

  const setRecording = (value: boolean) => {
    recording.value = value;
  };

  /** 画面フォーカス */
  const videoFocus = ref<VideoFocus>(VideoFocus.DEFAULT);

  const setVideoFocus = (value: VideoFocus): void => {
    videoFocus.value = value;
  };

  /** スマホライト点灯状態 */
  const lightState = ref(false);

  const setLightState = (value: boolean): void => {
    lightState.value = value;
  };

  /**
   * Storeの初期化
   */
  const clear = (): void => {
    meeting.value = undefined;
    meetingState.value = undefined;
    recording.value = false;

    clearChime();
  };

  const clearChime = (): void => {
    chimeState.value = { event: undefined, meetingStatus: undefined };
    contentShare.value = false;
    videoFocus.value = VideoFocus.DEFAULT;
    lightState.value = false;
  };

  return {
    /** 会議情報 */
    meeting,
    meetingRoomState,
    secondaryOrganizationMembers,
    setMeeting,
    /** 会議の状態 */
    meetingState,
    setMeetingState,
    /** Chimeの状態 */
    chimeState,
    setChimeState,
    isChimeMeetingStarted,
    isChimeMeetingEnded,
    /** 画面共有の状態 */
    contentShare,
    setContentShare,
    /** 録画の状態 */
    recording,
    setRecording,
    /** 画面フォーカス */
    videoFocus,
    setVideoFocus,
    /** スマホライト点灯状態 */
    lightState,
    setLightState,
    /** store初期化 */
    clear,
    clearChime
  };
});
